import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Building2, ArrowRight, Shield, UserPlus, CheckCircle, ArrowLeft, AlertCircle } from 'lucide-react';
import { authAPI } from '../lib/api';

export default function LoginPage() {
  const { user, login, register } = useAuth();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    if (searchParams.get('session') === 'expired') {
      setSessionExpired(true);
      window.history.replaceState({}, '', '/login');
    }
  }, [searchParams]);

  if (user) return <Navigate to="/dashboard" replace />;

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      if (!err.response) {
        setError('Unable to reach server. Please try again later.');
      } else if (err.response.status >= 500) {
        setError('Server is temporarily unavailable. Please try again in a moment.');
      } else {
        setError(err.response?.data?.detail || 'Authentication failed');
      }
    }
    setLoading(false);
  };

  const handleRequestAccess = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await authAPI.requestAccess({ email, name, reason });
      setMode('success');
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (detail.includes('already approved') || detail.includes('register directly')) {
        setError('');
        setMode('register');
      } else {
        setError(detail || 'Failed to submit request');
      }
    }
    setLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await register(email, password, name);
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed');
    }
    setLoading(false);
  };

  const inputClasses = "rounded-lg border-slate-200 focus:border-blue-500 focus:ring-blue-500/20 bg-slate-50/50 focus:bg-white h-11 transition-all duration-200";

  const renderLoginForm = () => (
    <div className="animate-fade-in">
      <h3 className="text-2xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Barlow Condensed', letterSpacing: '-0.01em' }}>
        Welcome Back
      </h3>
      <p className="text-slate-400 text-sm mb-8">
        Sign in to your account
      </p>

      {sessionExpired && (
        <div className="bg-amber-50 border border-amber-200/60 text-amber-700 rounded-lg px-4 py-3 mb-6 text-sm flex items-center gap-2" data-testid="session-expired-notice">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>Your session has expired. Please sign in again.</span>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200/60 text-red-600 rounded-lg px-4 py-3 mb-6 text-sm" data-testid="auth-error">
          {error}
        </div>
      )}

      <form onSubmit={handleLogin} className="space-y-5">
        <div>
          <Label htmlFor="email" className="text-slate-600 text-xs uppercase tracking-wider font-semibold mb-2 block">
            Email Address
          </Label>
          <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className={inputClasses} data-testid="login-email-input" required />
        </div>
        <div>
          <Label htmlFor="password" className="text-slate-600 text-xs uppercase tracking-wider font-semibold mb-2 block">
            Password
          </Label>
          <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password" className={inputClasses} data-testid="login-password-input" required />
          <div className="text-right mt-1.5">
            <a href="/forgot-password" className="text-xs text-slate-400 hover:text-blue-600 transition-colors" data-testid="forgot-password-link">
              Forgot password?
            </a>
          </div>
        </div>
        <Button type="submit" disabled={loading} className="w-full btn-primary h-11 rounded-lg text-sm" data-testid="login-submit-btn">
          {loading ? 'Signing in...' : 'Sign In'}
          {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
        </Button>
      </form>

      <div className="mt-8 pt-6 border-t border-slate-100">
        <p className="text-sm text-slate-400 mb-3 text-center">Don't have an account?</p>
        <Button variant="outline" onClick={() => { setMode('request'); setError(''); }} className="w-full rounded-lg border-blue-200/60 text-blue-600 hover:bg-blue-50/50 h-10" data-testid="request-access-btn">
          <UserPlus className="w-4 h-4 mr-2" /> Request Access
        </Button>
        <p className="text-center mt-3">
          <button onClick={() => { setMode('register'); setError(''); }} className="text-xs text-slate-400 hover:text-blue-600 transition-colors" data-testid="already-approved-btn">
            Already approved? Register here
          </button>
        </p>
      </div>
    </div>
  );

  const renderRequestForm = () => (
    <div className="animate-fade-in">
      <button onClick={() => { setMode('login'); setError(''); }} className="flex items-center gap-1 text-sm text-slate-400 hover:text-blue-600 mb-6 transition-colors">
        <ArrowLeft className="w-4 h-4" /> Back to login
      </button>
      <h3 className="text-2xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Barlow Condensed' }}>Request Access</h3>
      <p className="text-slate-400 text-sm mb-8">Submit your request and an admin will review it</p>
      {error && (<div className="bg-red-50 border border-red-200/60 text-red-600 rounded-lg px-4 py-3 mb-6 text-sm" data-testid="auth-error">{error}</div>)}
      <form onSubmit={handleRequestAccess} className="space-y-5">
        <div>
          <Label htmlFor="req-name" className="text-slate-600 text-xs uppercase tracking-wider font-semibold mb-2 block">Full Name</Label>
          <Input id="req-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="John Doe" className={inputClasses} data-testid="request-name-input" required />
        </div>
        <div>
          <Label htmlFor="req-email" className="text-slate-600 text-xs uppercase tracking-wider font-semibold mb-2 block">Email Address</Label>
          <Input id="req-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className={inputClasses} data-testid="request-email-input" required />
        </div>
        <div>
          <Label htmlFor="req-reason" className="text-slate-600 text-xs uppercase tracking-wider font-semibold mb-2 block">Reason <span className="text-slate-300 font-normal normal-case">(optional)</span></Label>
          <Textarea id="req-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Tell us why you need access..." className="rounded-lg border-slate-200 focus:border-blue-500 focus:ring-blue-500/20 bg-slate-50/50 focus:bg-white min-h-[80px] transition-all" data-testid="request-reason-input" />
        </div>
        <Button type="submit" disabled={loading} className="w-full btn-primary h-11 rounded-lg text-sm" data-testid="submit-request-btn">
          {loading ? 'Submitting...' : 'Submit Request'}
          {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
        </Button>
      </form>
    </div>
  );

  const renderSuccess = () => (
    <div className="text-center animate-scale-in">
      <div className="w-16 h-16 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-6">
        <CheckCircle className="w-8 h-8 text-emerald-500" />
      </div>
      <h3 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Barlow Condensed' }}>Request Submitted!</h3>
      <p className="text-slate-400 text-sm mb-8">Your access request has been submitted successfully.</p>
      <div className="bg-slate-50 border border-slate-100 rounded-lg p-4 mb-6 text-left">
        <p className="text-sm text-slate-600 font-medium">What happens next?</p>
        <ul className="text-sm text-slate-400 mt-2 space-y-1">
          <li>An administrator will review your request</li>
          <li>You'll receive an email when approved</li>
          <li>After approval, you can create your account</li>
        </ul>
      </div>
      <Button onClick={() => { setMode('login'); setEmail(''); setName(''); setReason(''); }} className="w-full btn-primary h-11 rounded-lg" data-testid="back-to-login-btn">
        <ArrowLeft className="w-4 h-4 mr-2" /> Back to Login
      </Button>
    </div>
  );

  const renderRegisterForm = () => (
    <div className="animate-fade-in">
      <button onClick={() => { setMode('login'); setError(''); }} className="flex items-center gap-1 text-sm text-slate-400 hover:text-blue-600 mb-6 transition-colors">
        <ArrowLeft className="w-4 h-4" /> Back to login
      </button>
      <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3 mb-6">
        <p className="text-sm text-emerald-700"><strong>Your email is approved!</strong> Create your account below.</p>
      </div>
      <h3 className="text-2xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Barlow Condensed' }}>Create Account</h3>
      <p className="text-slate-400 text-sm mb-8">Set up your password to complete registration</p>
      {error && (<div className="bg-red-50 border border-red-200/60 text-red-600 rounded-lg px-4 py-3 mb-6 text-sm" data-testid="auth-error">{error}</div>)}
      <form onSubmit={handleRegister} className="space-y-5">
        <div>
          <Label htmlFor="reg-name" className="text-slate-600 text-xs uppercase tracking-wider font-semibold mb-2 block">Full Name</Label>
          <Input id="reg-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="John Doe" className={inputClasses} data-testid="register-name-input" required />
        </div>
        <div>
          <Label htmlFor="reg-email" className="text-slate-600 text-xs uppercase tracking-wider font-semibold mb-2 block">Email Address</Label>
          <Input id="reg-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className={inputClasses} data-testid="register-email-input" required />
        </div>
        <div>
          <Label htmlFor="reg-password" className="text-slate-600 text-xs uppercase tracking-wider font-semibold mb-2 block">Password</Label>
          <Input id="reg-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Create a strong password" className={inputClasses} data-testid="register-password-input" required />
          <p className="text-xs text-slate-400 mt-1">Min 12 chars, uppercase, lowercase, and number required</p>
        </div>
        <Button type="submit" disabled={loading} className="w-full btn-primary h-11 rounded-lg text-sm" data-testid="register-submit-btn">
          {loading ? 'Creating account...' : 'Create Account'}
          {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
        </Button>
      </form>
    </div>
  );

  return (
    <div className="login-container" data-testid="login-page">
      <div className="login-left" style={{ backgroundImage: `url(https://images.unsplash.com/photo-1765062165737-ed4b47014fef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBvZmZpY2UlMjBidWlsZGluZyUyMGV4dGVyaW9yfGVufDB8fHx8MTc3MDgxODIzMXww&ixlib=rb-4.1.0&q=85)` }}>
        <div className="relative z-10 flex flex-col justify-between h-full p-12">
          <div className="flex items-center gap-2 opacity-70">
            <div className="w-8 h-8 rounded-lg bg-white/10 backdrop-blur-sm flex items-center justify-center border border-white/10">
              <Building2 className="w-4 h-4 text-white" />
            </div>
            <span className="text-white/70 text-sm font-medium tracking-wider" style={{ fontFamily: 'Barlow Condensed' }}>SIMLABDT</span>
          </div>
          <div>
            <h2 className="text-white text-5xl font-bold leading-[1.1] mb-5" style={{ fontFamily: 'Barlow Condensed', letterSpacing: '-0.02em' }}>
              COMMERCIAL<br />LEASE<br />MANAGEMENT
            </h2>
            <p className="text-blue-200/70 text-base max-w-md leading-relaxed">
              Enterprise-grade platform for managing rental properties, invoices, tenants, and analytics in one secure system.
            </p>
          </div>
        </div>
      </div>

      <div className="login-right">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-3 mb-10">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, hsl(221, 83%, 53%), hsl(221, 83%, 42%))', boxShadow: '0 4px 14px rgba(37, 99, 235, 0.25)' }}>
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed', letterSpacing: '0.04em' }}>
                SIMLABDT
              </h1>
              <span className="text-[9px] text-slate-400 uppercase tracking-[0.2em] font-semibold">Manager</span>
            </div>
          </div>

          {mode === 'login' && renderLoginForm()}
          {mode === 'request' && renderRequestForm()}
          {mode === 'register' && renderRegisterForm()}
          {mode === 'success' && renderSuccess()}

          {(mode !== 'success') && (
            <div className="mt-8 flex items-center gap-2 text-[11px] text-slate-300">
              <Shield className="w-3.5 h-3.5" />
              <span>Secured with JWT authentication</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
